/**
 * StructuredAnalysis — schema view for StructuredAssessmentV1.
 *
 * Composes a parts list + outer sections, reading from
 * `StructuredPartAssessment[]` and pairing with the marks-weighted
 * `partResults` for per-part display. Phase 1A: all parts are FREEFORM, so
 * the per-part render uses PartList's freeform-aware rendering.
 *
 * Approach summary + visual checks are admin-audience only.
 *
 * Phase 1B will likely fork a dedicated `StructuredPartCard` to render
 * objective parts (MCQ option selections, numerical answers) differently
 * from FREEFORM parts. For now the shared list works.
 */
import type { Feedback, StructuredPartAssessment } from '@padcanvas/shared';
import { VerdictBlock } from '../sections/VerdictBlock';
import { GatedNote } from '../sections/GatedNote';
import { PartList } from '../sections/PartList';
import { InterpretationBlock } from '../sections/InterpretationBlock';
import { VisualChecksList } from '../sections/VisualChecksList';
import { SuggestionsList } from '../sections/SuggestionsList';
import { HintsList } from '../sections/HintsList';
import { AdminOnlySection } from '../sections/AdminOnlySection';
import type { SolutionAnalysisAudience } from '../audience';

interface Props {
    feedback: Feedback;
    audience?: SolutionAnalysisAudience;
    /** Show the tutor-feedback v2 surfaces (verdict, per-part missing/wrong).
     *  Defaults to shown; the student app passes the `analysis_view_v2` gate. */
    analysisV2?: boolean;
    /** Accepted for a uniform dispatcher call. Structured questions render
     *  per-part results rather than a whole-attempt rationale, so there is
     *  nothing here to suppress. */
    questionType?: string | null;
}

export function StructuredAnalysis({ feedback, audience = 'admin', analysisV2 }: Props) {
    const a = feedback.llmAssessment;
    const parts: StructuredPartAssessment[] | undefined = a?.parts;
    const showAdminOnly = audience === 'admin';
    const showV2 = analysisV2 ?? true;
    return (
        <>
            {showV2 && (
                <>
                    {audience === 'admin' && <GatedNote gate="analysis_view_v2" />}
                    <VerdictBlock verdict={a?.verdict} />
                </>
            )}
            <PartList
                parts={parts}
                partResults={feedback.partResults}
                partScores={feedback.partScores}
                showMissingOrWrong={showV2}
                richSteps={showV2}
                lowTrust={feedback.trust?.level === 'low'}
                audience={audience}
            />
            {showAdminOnly && a?.interpretation?.approachSummary && (
                <AdminOnlySection>
                    <InterpretationBlock interpretation={a.interpretation} />
                </AdminOnlySection>
            )}
            {showAdminOnly && a?.visualChecks && a.visualChecks.length > 0 && (
                <AdminOnlySection>
                    <VisualChecksList items={a.visualChecks} />
                </AdminOnlySection>
            )}
            <HintsList items={a?.solutionGuidance} title="Overall Guidance" />
            <SuggestionsList items={a?.suggestionsGeneral} />
        </>
    );
}
